import React from 'react';
import {
  Box,
  Typography,
  Divider,
  MenuItem,
  Button,
  Tooltip,
} from '@mui/material';
import { useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router';
import { useSnackbar } from 'notistack';
import { Icon } from '@iconify/react';
import shieldCrown from '@iconify/icons-mdi/shield-crown';
import outlineManageAccounts from '@iconify/icons-ic/outline-manage-accounts';
import outlineSettings from '@iconify/icons-ic/outline-settings';
import { renderIcon } from 'src/constants/icons';
import useAuth from 'src/hooks/useAuth';
import { useRenterNotificationGate } from 'src/hooks/useRenterNotificationGate';
import { useRenterNotificationGateV2 } from 'src/hooks/useRenterNotificationGateV2';
import NotificationBell from 'src/components/Notifications/NotificationBell';
import WatchListEye from 'src/components/WatchList/WatchListEye';
import { StatusNotesBubble } from 'src/components/Compliance/Certification/StatusNotes/StatusNotesBubble';
import MenuPopover from 'src/components/MenuPopover';
import { TourButton as HeaderTourButton } from 'src/components/Header/HeaderTour';
import { Z_INDEX } from '../constants/zIndex';
import {
  PATH_DASHBOARD,
  COMPLIANCE_PATHS,
  USER_MANAGEMENT_PATHS,
} from 'src/routes/paths';
import { useEventTracking } from '../hooks/useEventTracking';

import { HeaderIconsProps, OpenDropdown } from 'src/types/Header';

const HeaderIcons = ({
  showNotificationBell = true,
  showSearch = true,
  showAccount = true,
  position = 'above',
}: HeaderIconsProps): React.JSX.Element => {
  const [openDropdown, setOpenDropdown] = useState<OpenDropdown>(null);
  const avatarButtonRef = useRef(null);
  const { enqueueSnackbar } = useSnackbar();
  const { user, logout } = useAuth();
  const legacyGate = useRenterNotificationGate();
  const v2Gate = useRenterNotificationGateV2();
  const renterNotificationsEnabled = user?.sees_new_experience
    ? v2Gate.isEnabled
    : legacyGate.isEnabled;
  const { trackHeaderIconClick, trackDropdownAction } = useEventTracking();
  const isResident = Boolean(
    user?.isResident ||
      user?.role === 'resident' ||
      user?.groups?.some((group) => group?.name?.toLowerCase() === 'resident')
  );
  const showComplianceNotifications =
    user?.canUseRealTimeNotificationsFileOwner ||
    user?.canUseRealTimeNotificationsWatched ||
    user?.canUseRealTimeNotificationsProperty;
  const showRenterNotifications = isResident && renterNotificationsEnabled;

  // Build menu options
  const menuOptions = [];
  if (user?.isProntoAdmin) {
    menuOptions.push({
      label: 'Pronto Administration',
      icon: shieldCrown,
      linkTo: PATH_DASHBOARD.root,
    });
  }
  if (user?.isUserAdministrator) {
    menuOptions.push({
      label: 'User Management',
      icon: outlineManageAccounts,
      linkTo: USER_MANAGEMENT_PATHS.root,
    });
  }
  if (user?.isCompliance) {
    menuOptions.push({
      label: 'Team Renters',
      icon: 'family',
      linkTo: COMPLIANCE_PATHS.applicants.teamApplicants,
    });
  }
  menuOptions.push({
    label: 'User Settings',
    icon: outlineSettings,
    linkTo: user?.isCompliance
      ? COMPLIANCE_PATHS.user.settings
      : '/user/settings',
  });
  // Support agent console moved to the left sidebar (ComplianceSidebarConfig,
  // "Resources" section, Support-group gated) alongside Help and Resources.
  // Shared dropdown management functions
  const handleDropdownToggle = (dropdownType: OpenDropdown): void => {
    if (openDropdown === dropdownType) {
      // If clicking the same dropdown, close it (don't track)
      setOpenDropdown(null);
    } else {
      // If clicking a different dropdown, close the current one and open the new one
      // Track header icon click only when opening
      trackHeaderIconClick(dropdownType, {
        action: 'drop_down_open',
        previous_page: openDropdown,
        icon_type: dropdownType,
        user_role: user?.role || 'unknown',
      });
      setOpenDropdown(dropdownType);
    }
  };

  const handleDropdownClose = (): void => {
    setOpenDropdown(null);
  };

  const handleAvatarClick = (): void => {
    handleDropdownToggle('account');
  };

  const handleLogout = async (): Promise<void> => {
    try {
      await logout();
      handleDropdownClose();
    } catch {
      enqueueSnackbar('Unable to logout.', { variant: 'error' });
    }
  };

  const containerStyles =
    position === 'above'
      ? {
          position: 'fixed' as const,
          top: 8,
          right: 24,
          zIndex: Z_INDEX.HEADER_ICONS,
          display: 'flex',
          flexDirection: 'row' as const,
          alignItems: 'center',
          gap: 2,
          pointerEvents: 'auto' as const,
          backgroundColor: 'white',
          borderRadius: 2,
          padding: '8px 12px',
          boxShadow: 1,
          // Ensure icons are clickable
          cursor: 'pointer',
        }
      : {
          display: 'flex',
          flexDirection: 'row' as const,
          alignItems: 'center',
          gap: 2,
          pointerEvents: 'auto' as const,
        };

  return (
    <Box sx={containerStyles} data-tour="header-container">
      {/* Header Tour Button - Only show for compliance users, not for renters */}
      {user?.isCompliance &&
        !user?.groups?.some(
          (group) => group.name.toLowerCase() === 'resident'
        ) && (
          <Box data-tour="tour-button">
            <HeaderTourButton />
          </Box>
        )}

      {/* Search Icon - Integrated with Status Notes functionality - Only show for compliance users */}
      {showSearch && user?.isCompliance && (
        <Tooltip title="Search & Status Notes" placement="bottom">
          <Box data-tour="search-status-notes">
            <StatusNotesBubble
              bigIcon={true}
              isOpen={openDropdown === 'search'}
              onToggle={() => handleDropdownToggle('search')}
              onClose={handleDropdownClose}
            />
          </Box>
        </Tooltip>
      )}

      {/* Watch List Eye */}
      {user?.canUseRealTimeNotificationsWatched && (
        <Box data-tour="watchlist-eye">
          <WatchListEye
            iconSize={36}
            isOpen={openDropdown === 'watchlist'}
            onToggle={() => handleDropdownToggle('watchlist')}
            onClose={handleDropdownClose}
          />
        </Box>
      )}

      {/* Notification Bell */}
      {showNotificationBell &&
        (showComplianceNotifications || showRenterNotifications) && (
          <Box data-tour="notification-bell">
            <NotificationBell
              iconSize={36}
              isOpen={openDropdown === 'notifications'}
              onToggle={() => handleDropdownToggle('notifications')}
              onClose={handleDropdownClose}
              notificationSourceFilter={
                showRenterNotifications ? 'renter' : undefined
              }
            />
          </Box>
        )}

      {/* Account Avatar */}
      {showAccount && (
        <Box data-tour="account-avatar">
          <Box
            ref={avatarButtonRef}
            onClick={handleAvatarClick}
            sx={{
              background: 'white',
              borderRadius: '50%',
              width: 48,
              height: 48,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: 'none',
              outline: 'none',
              p: 0,
              m: 0,
              userSelect: 'none',
              cursor: 'pointer',
              border: '1px solid',
              borderColor: 'divider',
            }}
            tabIndex={0}
          >
            <Box
              sx={{
                fontSize: 24,
                fontWeight: 700,
                color: '#1E88E5',
                userSelect: 'none',
              }}
            >
              {user?.displayName?.[0] || 'U'}
            </Box>
          </Box>
          <MenuPopover
            open={openDropdown === 'account'}
            onClose={handleDropdownClose}
            anchorEl={avatarButtonRef.current}
            sx={{ minWidth: 320, zIndex: Z_INDEX.HEADER_ICONS }}
            container={document.body}
            slotProps={{
              paper: {
                sx: {
                  zIndex: Z_INDEX.HEADER_DROPDOWN,
                },
              },
            }}
          >
            <Box sx={{ my: 1.5, px: 2.5 }}>
              <Typography variant="subtitle1" noWrap>
                {user?.first_name || user?.displayName}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary' }}
                noWrap
              >
                {user?.email}
              </Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            {menuOptions.map((option) => (
              <MenuItem
                key={option.label}
                to={option.linkTo}
                component={RouterLink}
                onClick={() => {
                  trackDropdownAction('account', 'link_click', {
                    navigation_item: option.label
                      .toLowerCase()
                      .replace(/\s+/g, '_'),
                    navigation_method: 'header',
                  });
                  handleDropdownClose();
                }}
                sx={{ typography: 'body2', py: 1, px: 2.5 }}
              >
                {typeof option.icon === 'string' && option.icon === 'family' ? (
                  <Box
                    sx={{
                      mr: 2,
                      width: 36,
                      height: 36,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {renderIcon('header', 'family', {
                      sx: { width: 24, height: 24 },
                    })}
                  </Box>
                ) : (
                  <Box
                    component={Icon}
                    icon={option.icon}
                    sx={{ mr: 2, width: 36, height: 36 }}
                  />
                )}
                {option.label}
              </MenuItem>
            ))}
            <Box sx={{ p: 2, pt: 1.5 }}>
              <Button
                fullWidth
                color="inherit"
                variant="outlined"
                onClick={() => {
                  trackDropdownAction('account', 'logout');
                  handleLogout();
                }}
              >
                Logout
              </Button>
            </Box>
          </MenuPopover>
        </Box>
      )}
    </Box>
  );
};

export default HeaderIcons;
